/**
 * @fileoverview ChatInput - Premium chat composer
 *
 * Rounded input bar with subtle elevation. Enter to send, Shift+Enter for
 * newlines. Animated send button with loading state.
 *
 * Voice dictation: while the composer is empty the action button is a
 * microphone (styled like the send button for consistency). Clicking it
 * starts a live voice transcription session — the streamed text appears
 * in the box in real time. As soon as there is text to send, the button
 * flips to the send arrow so users who prefer typing get the familiar
 * send affordance.
 *
 * Attachments & @-mentions (MOR-890): hosts can opt into a plus button
 * (`onAttachClick`) that opens their file picker, a chip row for the
 * currently attached files (`attachments` / `onRemoveAttachment`), and an
 * `@`-mention dropdown over `mentionItems`. Typing `@` opens a filtered
 * list; selecting an item inserts `@Name ` into the text and fires
 * `onMentionSelect` so the host can add it to the attachment set.
 */

import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { cn } from "@/utils/misc";
// Imported directly rather than via `@/ui/composed`, whose barrel re-exports
// `./chat` and would make this a circular import.
import { AiDisclaimer } from "../ai-disclaimer";
import { ArrowUp, FileText, Loader2, Mic, Plus, Square, X } from "lucide-react";
import {
  useChatVoiceInput,
  appendTranscript,
} from "@/ui/hooks/use-chat-voice-input";
import { filterMentionItems, findActiveMention } from "./mention";
import { segmentMentions } from "./messageTags";

/** A file chip shown on the composer (picker- or mention-attached). */
export interface ChatComposerAttachment {
  id: string;
  name: string;
  /** e.g. the owning job's name in workspace scope. */
  subtitle?: string;
}

/** An item offered by the `@`-mention dropdown. */
export interface ChatMentionItem {
  id: string;
  name: string;
  subtitle?: string;
}

interface ChatInputProps {
  onSend: (message: string) => void;
  isDisabled?: boolean;
  placeholder?: string;
  className?: string;
  /**
   * Optional text to pre-fill the composer with (e.g. "Draft with Morgan"
   * seeding the mail chat from the email composer). Applied whenever the
   * value changes to a non-empty string; the user can still edit before
   * sending.
   */
  initialValue?: string;
  /** Files currently attached to the draft message — rendered as chips. */
  attachments?: ChatComposerAttachment[];
  onRemoveAttachment?: (id: string) => void;
  /** When set, a plus button opens the host's file picker. */
  onAttachClick?: () => void;
  /** Documents offered by the `@`-mention dropdown. */
  mentionItems?: ChatMentionItem[];
  onMentionSelect?: (item: ChatMentionItem) => void;
  /** Header label for the `@`-mention dropdown. */
  mentionLabel?: string;
  /**
   * "chat" (default) — the full composer: Enter sends, with the send/mic
   * button, helper text, and AI disclaimer.
   * "field" — an embedded input (e.g. inside a dialog) that keeps the
   * attachment/@-mention affordances but has no send button, Enter inserts a
   * newline, and the host reads the text via `onValueChange`.
   */
  variant?: "chat" | "field";
  /** Fires with the current text whenever it changes (used by "field" hosts). */
  onValueChange?: (value: string) => void;
  /** Extra classes for the textarea itself. */
  inputClassName?: string;
  /**
   * How tall the composer may grow before it scrolls. The box always starts
   * at one line and grows with the text — this only raises the ceiling for
   * hosts with room for more (e.g. a dialog).
   */
  maxHeightPx?: number;
}

const MAX_MENTION_RESULTS = 8;

export function ChatInput({
  onSend,
  isDisabled,
  placeholder = "Ask a question about this summary...",
  className,
  initialValue,
  attachments,
  onRemoveAttachment,
  onAttachClick,
  mentionItems,
  onMentionSelect,
  mentionLabel = "Tag a document",
  variant = "chat",
  onValueChange,
  inputClassName,
  maxHeightPx = 140,
}: ChatInputProps) {
  const [value, setValue] = useState(initialValue ?? "");
  const isField = variant === "field";

  // Mirror the committed text out to "field" hosts (dialogs read it instead
  // of receiving a send event).
  useEffect(() => {
    onValueChange?.(value);
  }, [value, onValueChange]);
  const [caretPos, setCaretPos] = useState(0);
  // `@` index the user dismissed with Escape — keeps the dropdown closed
  // until they start a new token.
  const [mentionDismissedAt, setMentionDismissedAt] = useState<number | null>(
    null,
  );
  const [mentionIndex, setMentionIndex] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const highlightRef = useRef<HTMLDivElement>(null);

  // Re-seed the composer when the parent hands down a new pre-fill (e.g. the
  // user clicks "Draft with Morgan" again with different email content).
  useEffect(() => {
    if (initialValue && initialValue.trim().length > 0) {
      setValue(initialValue);
      requestAnimationFrame(() => textareaRef.current?.focus());
    }
  }, [initialValue]);

  const voice = useChatVoiceInput({
    onFinal: (transcript) =>
      setValue((prev) => appendTranscript(prev, transcript)),
  });

  // ---------------------------------------------------------------------
  // @-mention dropdown
  // ---------------------------------------------------------------------
  const activeMention =
    !voice.isActive && mentionItems && mentionItems.length > 0
      ? findActiveMention(value, caretPos)
      : null;
  const mentionMatches = useMemo(() => {
    if (!activeMention || !mentionItems) return [];
    return filterMentionItems(mentionItems, activeMention.query, {
      text: value,
      attachedIds: (attachments ?? []).map((a) => a.id),
      limit: MAX_MENTION_RESULTS,
    });
  }, [activeMention, mentionItems, attachments, value]);
  const mentionOpen =
    !!activeMention &&
    mentionMatches.length > 0 &&
    mentionDismissedAt !== activeMention.start;

  useEffect(() => {
    setMentionIndex(0);
  }, [activeMention?.start, activeMention?.query]);

  const handleMentionSelect = useCallback(
    (item: ChatMentionItem) => {
      if (!activeMention) return;
      const before = value.slice(0, activeMention.start);
      const after = value.slice(caretPos);
      const inserted = `@${item.name} `;
      const next = before + inserted + after;
      setValue(next);
      onMentionSelect?.(item);
      const newCaret = before.length + inserted.length;
      setCaretPos(newCaret);
      requestAnimationFrame(() => {
        const el = textareaRef.current;
        if (el) {
          el.focus();
          el.setSelectionRange(newCaret, newCaret);
        }
      });
    },
    [activeMention, value, caretPos, onMentionSelect],
  );

  const handleSend = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed || isDisabled || voice.isActive) return;
    onSend(trimmed);
    setValue("");
    setCaretPos(0);
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  }, [value, isDisabled, voice.isActive, onSend]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (mentionOpen) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setMentionIndex((i) => (i + 1) % mentionMatches.length);
          return;
        }
        if (e.key === "ArrowUp") {
          e.preventDefault();
          setMentionIndex(
            (i) => (i - 1 + mentionMatches.length) % mentionMatches.length,
          );
          return;
        }
        if (e.key === "Enter" || e.key === "Tab") {
          e.preventDefault();
          handleMentionSelect(mentionMatches[mentionIndex]);
          return;
        }
        if (e.key === "Escape") {
          e.preventDefault();
          setMentionDismissedAt(activeMention?.start ?? null);
          return;
        }
      }
      if (e.key === "Enter" && !e.shiftKey && !isField) {
        e.preventDefault();
        handleSend();
      }
    },
    [
      mentionOpen,
      mentionMatches,
      mentionIndex,
      activeMention,
      handleMentionSelect,
      handleSend,
      isField,
    ],
  );

  const handleInput = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setValue(e.target.value);
      setCaretPos(e.target.selectionStart ?? e.target.value.length);
      setMentionDismissedAt(null);
      const textarea = e.target;
      textarea.style.height = "auto";
      textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeightPx)}px`;
    },
    [maxHeightPx],
  );

  // While listening, stream the interim transcript into the box. The
  // textarea is read-only during a session, so resize it from an effect
  // rather than the (suppressed) onChange handler.
  const displayValue = voice.isActive
    ? appendTranscript(value, voice.interim)
    : value;

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeightPx)}px`;
    if (highlightRef.current) {
      highlightRef.current.scrollTop = textarea.scrollTop;
    }
  }, [displayValue, maxHeightPx]);

  // Highlight layer segments: `@FileName` tokens for currently attached
  // files get a tinted chip background painted behind the (transparent-
  // background) textarea, so typed mentions read as tags while staying
  // plain editable text.
  const highlightSegments = useMemo(() => {
    // Attached files plus any other taggable item (e.g. a report section)
    // whose `@Name` the user has typed — sections have no chip row, so the
    // backdrop is the only signal that the tag registered.
    const names = [
      ...(attachments ?? []).map((a) => a.name),
      ...(mentionItems ?? []).map((i) => i.name),
    ];
    if (names.length === 0) return null;
    const segments = segmentMentions(displayValue, names);
    return segments.some((s) => s.type === "mention") ? segments : null;
  }, [displayValue, attachments, mentionItems]);

  const hasValue = value.trim().length > 0;

  // Action-button mode, in precedence order:
  //  - sending  → spinner (existing behaviour, preserved)
  //  - processing voice → spinner, disabled
  //  - listening → stop (red square)
  //  - has text → send arrow
  //  - empty   → microphone, or the send arrow (inert) when this deployment
  //    has dictation off
  type ButtonMode = "sending" | "processing" | "listening" | "send" | "mic";
  const buttonMode: ButtonMode = isDisabled
    ? "sending"
    : voice.isProcessing
      ? "processing"
      : voice.isListening
        ? "listening"
        : hasValue || !voice.isEnabled
          ? "send"
          : "mic";

  const handleButtonClick = useCallback(() => {
    if (buttonMode === "send") {
      handleSend();
    } else if (buttonMode === "mic" || buttonMode === "listening") {
      voice.toggle();
    }
  }, [buttonMode, handleSend, voice]);

  const buttonLabel: Record<ButtonMode, string> = {
    sending: "Sending",
    processing: "Finalising transcript",
    listening: "Stop dictation",
    send: "Send message",
    mic: "Dictate message",
  };

  const helperText =
    buttonMode === "listening"
      ? "Listening… tap to stop"
      : buttonMode === "processing"
        ? "Finalising transcript…"
        : mentionItems && mentionItems.length > 0
          ? `Press Enter to send • @ to ${mentionLabel.toLowerCase()}`
          : "Press Enter to send, Shift+Enter for new line";

  // Per-mode colour classes kept in a lookup so each string is
  // internally consistent (avoids cross-branch Tailwind conflicts).
  const buttonColorClass: Record<ButtonMode, string> = {
    sending: "bg-muted text-muted-foreground",
    processing: "bg-muted text-muted-foreground",
    listening:
      "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
    send: "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90",
    mic: "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90",
  };

  return (
    <div className={cn("px-4 pb-4 pt-2", className)}>
      <div className="relative rounded-xl border border-border bg-background shadow-sm transition-shadow focus-within:border-primary/40 focus-within:shadow-md">
        {/* @-mention dropdown — anchored above the composer. */}
        {mentionOpen && (
          <div className="absolute bottom-full left-0 z-50 mb-2 w-full max-w-md overflow-hidden rounded-lg border border-border bg-popover shadow-lg">
            <p className="border-b border-border px-3 py-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60">
              {mentionLabel}
            </p>
            <div className="max-h-64 overflow-y-auto py-1">
              {mentionMatches.map((item, i) => (
                <button
                  key={item.id}
                  type="button"
                  // onMouseDown so the textarea keeps focus.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleMentionSelect(item);
                  }}
                  onMouseEnter={() => setMentionIndex(i)}
                  className={cn(
                    "flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm",
                    i === mentionIndex
                      ? "bg-accent/30 text-foreground"
                      : "text-muted-foreground",
                  )}
                >
                  <FileText className="h-3.5 w-3.5 shrink-0 opacity-60" />
                  <span className="min-w-0 flex-1 truncate">{item.name}</span>
                  {item.subtitle && (
                    <span className="max-w-[160px] shrink-0 truncate text-[10px] text-muted-foreground/60">
                      {item.subtitle}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Attachment chips for the draft message. */}
        {attachments && attachments.length > 0 && (
          <div className="flex flex-wrap gap-1.5 px-3 pt-2.5">
            {attachments.map((attachment) => (
              <span
                key={attachment.id}
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-muted/40 px-2 py-1 text-xs"
              >
                <FileText className="h-3 w-3 shrink-0 text-muted-foreground" />
                <span className="max-w-[180px] truncate">
                  {attachment.name}
                </span>
                {attachment.subtitle && (
                  <span className="max-w-[120px] truncate text-muted-foreground/60">
                    {attachment.subtitle}
                  </span>
                )}
                {onRemoveAttachment && (
                  <button
                    type="button"
                    onClick={() => onRemoveAttachment(attachment.id)}
                    aria-label={`Remove ${attachment.name}`}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </span>
            ))}
          </div>
        )}

        <div className="flex items-end gap-2">
          {onAttachClick && (
            <button
              type="button"
              onClick={onAttachClick}
              disabled={isDisabled}
              aria-label="Attach files"
              title="Attach files"
              className={cn(
                "mb-2 ml-2 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors",
                "hover:bg-muted hover:text-foreground",
                "disabled:cursor-not-allowed disabled:opacity-40",
              )}
            >
              <Plus className="h-4 w-4" />
            </button>
          )}
          <div className="relative min-w-0 flex-1">
            {/* Chip-style backdrop for attached-file mentions. Mirrors the
                textarea's typography/padding exactly; text is transparent so
                only the rounded highlight shows through the transparent
                textarea above it. */}
            {highlightSegments && (
              <div
                ref={highlightRef}
                aria-hidden
                className={cn(
                  "pointer-events-none absolute inset-0 overflow-hidden",
                  "whitespace-pre-wrap break-words py-3 text-sm text-transparent",
                  onAttachClick ? "pl-1 pr-4" : "px-4",
                )}
              >
                {highlightSegments.map((segment, i) =>
                  segment.type === "text" ? (
                    <span key={i}>{segment.text}</span>
                  ) : (
                    <span
                      key={i}
                      className="rounded bg-primary/15 box-decoration-clone ring-1 ring-inset ring-primary/25"
                    >
                      @{segment.name}
                    </span>
                  ),
                )}
                {"\n"}
              </div>
            )}
            <textarea
              ref={textareaRef}
              value={displayValue}
              onChange={handleInput}
              onKeyDown={handleKeyDown}
              onScroll={(e) => {
                if (highlightRef.current) {
                  highlightRef.current.scrollTop = e.currentTarget.scrollTop;
                }
              }}
              onSelect={(e) =>
                setCaretPos(
                  (e.target as HTMLTextAreaElement).selectionStart ?? 0,
                )
              }
              placeholder={
                voice.isListening ? "Listening — start speaking…" : placeholder
              }
              disabled={isDisabled}
              readOnly={voice.isActive}
              rows={1}
              style={{ maxHeight: maxHeightPx }}
              className={cn(
                "relative block w-full resize-none bg-transparent py-3 text-sm",
                onAttachClick ? "pl-1 pr-4" : "px-4",
                "placeholder:text-muted-foreground/60",
                "focus-visible:outline-none",
                "disabled:cursor-not-allowed disabled:opacity-50",
                "min-h-[44px]",
                inputClassName,
              )}
            />
          </div>
          {!isField && (
            <button
              type="button"
              onClick={handleButtonClick}
              disabled={
                buttonMode === "sending" ||
                buttonMode === "processing" ||
                (buttonMode === "send" && !hasValue)
              }
              aria-label={buttonLabel[buttonMode]}
              aria-pressed={buttonMode === "listening"}
              title={buttonLabel[buttonMode]}
              className={cn(
                "mb-2 mr-2 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-all",
                buttonColorClass[buttonMode],
                "disabled:cursor-not-allowed disabled:opacity-40",
              )}
            >
              {buttonMode === "sending" || buttonMode === "processing" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : buttonMode === "listening" ? (
                <Square className="h-3.5 w-3.5 fill-current" />
              ) : buttonMode === "mic" ? (
                <Mic className="h-4 w-4" />
              ) : (
                <ArrowUp className="h-4 w-4" />
              )}
            </button>
          )}
        </div>
      </div>
      {!isField && (
        <>
          <p
            className={cn(
              "mt-1.5 text-center text-[10px]",
              buttonMode === "listening"
                ? "text-destructive/70"
                : "text-muted-foreground/40",
            )}
          >
            {helperText}
          </p>
          <AiDisclaimer className="mt-1 text-center" />
        </>
      )}
    </div>
  );
}
